/**
 * One category, as a board.
 *
 * The board itself lives in CategoryBoard and is shared with the dashboard, so
 * the two screens cannot drift apart. This page adds the breadcrumb, the
 * larger header, and the data loading for a single category.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { ApiError, api } from '@/api/client'
import type { Category, Item, ItemCreatePayload, ItemPriority, Subcategory } from '@/api/types'
import { AddTaskModal } from '@/components/AddTaskModal'
import { CategoryBoard } from '@/components/CategoryBoard'
import { useToast } from '@/components/Toast'
import { ErrorState, LoadingBlock } from '@/components/ui'
import { todayIso } from '@/lib/format'

export function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>()
  const id = Number(categoryId)
  const toast = useToast()

  const [category, setCategory] = useState<Category | null>(null)
  const [allCategories, setAllCategories] = useState<Category[]>([])
  const [items, setItems] = useState<Item[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [today, setToday] = useState(todayIso())
  const [addingTo, setAddingTo] = useState<number | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  const load = useCallback(
    async (options: { quiet?: boolean } = {}) => {
      if (!Number.isFinite(id)) {
        setError('That is not a valid category.')
        setLoading(false)
        return
      }
      if (!options.quiet) setLoading(true)
      try {
        const [cats, list, summary] = await Promise.all([
          api.categories.list(true),
          api.items.list({ category_id: id }),
          api.dashboard.summary(),
        ])
        const found = cats.find((c) => c.id === id) ?? null
        if (!found) {
          setError('That category could not be found.')
          return
        }
        setAllCategories(cats)
        setCategory(found)
        setItems(list.items)
        setToday(summary.today)
        setError(null)
      } catch (caught) {
        if (caught instanceof ApiError && !caught.isUnauthorized) setError(caught.message)
      } finally {
        setLoading(false)
      }
    },
    [id],
  )

  useEffect(() => {
    void load()
  }, [load])

  async function saveSubcategoryStatus(sub: Subcategory, next: string | null) {
    // Applied locally first so the text does not flicker back while the
    // request is in flight.
    setCategory((current) =>
      current
        ? {
            ...current,
            subcategories: current.subcategories.map((s) =>
              s.id === sub.id ? { ...s, status_note: next } : s,
            ),
          }
        : current,
    )
    try {
      await api.subcategories.update(sub.id, { status_note: next })
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not save that status.')
      await load({ quiet: true })
    }
  }

  async function saveItemStatus(item: Item, next: string | null) {
    setItems((current) => current.map((i) => (i.id === item.id ? { ...i, status_note: next } : i)))
    try {
      await api.items.update(item.id, { status_note: next })
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not save that status.')
      await load({ quiet: true })
    }
  }

  async function complete(item: Item) {
    const snapshot = items
    setItems((current) => current.filter((i) => i.id !== item.id))
    try {
      await api.items.update(item.id, { status: 'done' })
      await load({ quiet: true })
      toast.show(`Marked "${item.title}" done`, {
        tone: 'success',
        action: {
          label: 'Undo',
          onClick: () => {
            void api.items
              .update(item.id, { status: item.status })
              .then(() => load({ quiet: true }))
              .catch(() => toast.error('Could not undo that.'))
          },
        },
      })
    } catch (caught) {
      setItems(snapshot)
      toast.error(caught instanceof ApiError ? caught.message : 'Could not update that item.')
    }
  }

  async function changePriority(item: Item, priority: ItemPriority) {
    setItems((current) => current.map((i) => (i.id === item.id ? { ...i, priority } : i)))
    try {
      await api.items.update(item.id, { priority })
      await load({ quiet: true })
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not change that priority.')
      await load({ quiet: true })
    }
  }

  async function reorderSubcategories(ids: number[]) {
    // Applied locally first so the tile stays where it was dropped rather than
    // snapping back while the request is in flight.
    setCategory((current) =>
      current
        ? {
            ...current,
            subcategories: ids
              .map((sid) => current.subcategories.find((s) => s.id === sid))
              .filter((s): s is Subcategory => s !== undefined),
          }
        : current,
    )
    try {
      await api.subcategories.reorder(ids)
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not save that order.')
      await load({ quiet: true })
    }
  }

  async function addTask(payload: ItemCreatePayload) {
    const created = await api.items.create(payload)
    await load({ quiet: true })
    toast.success(`Added "${created.title}"`)
  }

  if (loading) return <LoadingBlock label="Loading category" />
  if (error || !category) {
    return <ErrorState message={error ?? 'Category not found.'} onRetry={() => void load()} />
  }

  return (
    <div className="flex flex-col gap-4">
      <nav className="flex items-center gap-2 px-1 text-xs">
        <Link to="/" className="text-navy hover:underline">
          Dashboard
        </Link>
        <span aria-hidden className="text-ink-3">
          &rsaquo;
        </span>
        <span className="text-ink-3">{category.name}</span>
      </nav>

      <CategoryBoard
        category={category}
        items={items}
        today={today}
        size="page"
        onSubcategoryStatus={saveSubcategoryStatus}
        onItemStatus={saveItemStatus}
        onComplete={(item) => void complete(item)}
        onPriorityChange={(item, priority) => void changePriority(item, priority)}
        onAddTask={(subcategoryId) => {
          setAddingTo(subcategoryId)
          setModalOpen(true)
        }}
        onReorderSubcategories={(ids) => void reorderSubcategories(ids)}
      />

      <AddTaskModal
        open={modalOpen}
        categories={allCategories}
        defaultSubcategoryId={addingTo}
        onClose={() => setModalOpen(false)}
        onCreate={addTask}
      />
    </div>
  )
}
